import { useState, useEffect, useCallback, useMemo, Component } from 'react';
import { useSearchParams } from 'react-router-dom';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import ProductSelector from './ProductSelector';
import MostReviewedWidget from './widgets/MostReviewedWidget';
import BestWorstQuarterWidget from './widgets/BestWorstQuarterWidget';
import StrugglingStoresWidget from './widgets/StrugglingStoresWidget';
import useDimensionData from '../hooks/useDimensionData';
import { DIMENSIONS } from '../constants/dimensions';
import '../styles/DimensionDashboardsComposed.css';

const WIDGET_LABELS = {
    'most-reviewed': 'Most Reviewed',
    'best-worst': 'Quarter over Quarter',
    struggling: 'Needs Attention',
};

// ---------------------------------------------------------------------------
// Error Boundary (per-widget)
// ---------------------------------------------------------------------------
class WidgetErrorBoundary extends Component {
    state = { hasError: false };
    static getDerivedStateFromError() { return { hasError: true }; }
    componentDidCatch(err) { console.error('[WidgetErrorBoundary]', err); }
    render() {
        if (this.state.hasError) {
            return (
                <div className="dim-widget-error">
                    <div>Unable to load this insight.</div>
                    <button onClick={() => this.setState({ hasError: false })}>Retry</button>
                </div>
            );
        }
        return this.props.children;
    }
}

// ---------------------------------------------------------------------------
// Skeleton Loader
// ---------------------------------------------------------------------------
function WidgetSkeleton() {
    return (
        <div className="dim-widget-skeleton">
            <div className="skeleton-line title" />
            <div className="skeleton-line headline" />
            <div className="skeleton-line row" />
            <div className="skeleton-line row" />
            <div className="skeleton-line row" />
            <div className="skeleton-line footer" />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main Composed Dashboard
// ---------------------------------------------------------------------------
export default function DimensionDashboardsComposed({ topicMap,
    // Optional shared product selection: adopted when valid for this
    // page's topicMap; selections made here are pushed back up.
    selectedProduct: sharedSelectedProduct, onProductChange }) {
    // Lazy-initialize from the shared selection (if valid) so local state
    // is never empty on first render — see PricingPlot.js for why an
    // empty initial value lets ProductSelector's own internal auto-select
    // effect win a race against the shared-selection logic below.
    const [selectedTopic, setSelectedTopic] = useState(() =>
        (sharedSelectedProduct && topicMap?.[sharedSelectedProduct]) ? sharedSelectedProduct : null
    );
    const [dimension, setDimension] = useState('location');

    // Select a default topic if none is selected yet. Prefers the
    // page-wide shared topic selection (if valid here); otherwise falls
    // back to the highest-priority topic. See DimensionNPSPage.js for why
    // this replaces relying on ProductSelector's own internal auto-select.
    useEffect(() => {
        if (selectedTopic) return;
        if (sharedSelectedProduct && topicMap?.[sharedSelectedProduct]) {
            setSelectedTopic(sharedSelectedProduct);
            return;
        }
        const sortedTopics = Object.values(topicMap || {}).sort((a, b) => {
            const priorityA = a.priority === undefined ? Infinity : a.priority;
            const priorityB = b.priority === undefined ? Infinity : b.priority;
            return priorityA - priorityB;
        });
        if (sortedTopics[0]) setSelectedTopic(sortedTopics[0].id);
    }, [sharedSelectedProduct, topicMap, selectedTopic]);

    const selectTopic = (topicId) => {
        setSelectedTopic(topicId);
        if (onProductChange) onProductChange(topicId);
    };
    const [searchParams, setSearchParams] = useSearchParams();

    // URL-synced active widget
    const widgetParam = searchParams.get('widget');
    const activeWidget = widgetParam && WIDGET_LABELS[widgetParam] ? widgetParam : null;
    const setActiveWidget = useCallback((widget) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (widget) {
                next.set('widget', widget);
            } else {
                next.delete('widget');
            }
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const { npsData, quarterlyData, loading, errors } = useDimensionData({
        topicId: selectedTopic,
        dimension,
    });

    // Derived data for widgets
    const mostReviewedData = useMemo(() =>
        [...npsData].sort((a, b) => b.count - a.count).slice(0, 3),
    [npsData]);

    const allStrugglingData = useMemo(() =>
        npsData.filter(r => r.nps < 0 && r.count >= 10).sort((a, b) => a.nps - b.nps),
    [npsData]);

    const strugglingData = useMemo(() => allStrugglingData.slice(0, 3), [allStrugglingData]);

    const latestQuarterData = useMemo(() => {
        if (quarterlyData.length === 0) return { gains: [], drops: [], quarter: null, prevQuarter: null };
        // Find the latest quarter that has delta values
        const withDelta = quarterlyData.filter(r => r.delta != null);
        if (withDelta.length === 0) return { gains: [], drops: [], quarter: quarterlyData[0]?.quarter, prevQuarter: null };
        const latestQ = withDelta[0].quarter; // already sorted DESC by backend
        const qRows = withDelta.filter(r => r.quarter === latestQ);
        const gains = [...qRows].sort((a, b) => b.delta - a.delta).slice(0, 3);
        const drops = [...qRows].sort((a, b) => a.delta - b.delta).filter(r => r.delta < 0).slice(0, 3);
        // Find the previous quarter label from any row
        const prevQuarter = qRows[0]?.prev_nps != null ? findPrevQuarter(latestQ) : null;
        return { gains, drops, quarter: latestQ, prevQuarter };
    }, [quarterlyData]);

    const dimConfig = DIMENSIONS.find(d => d.value === dimension) || DIMENSIONS[0];

    const handleCardClick = useCallback((widgetId) => {
        setActiveWidget(activeWidget === widgetId ? null : widgetId);
    }, [activeWidget, setActiveWidget]);

    // No topic selected state
    if (!selectedTopic && !Object.keys(topicMap || {}).length) {
        return (
            <div className="dim-composed-container">
                <div className="dim-widget-empty">
                    <div className="empty-icon">📊</div>
                    Select a product above to see dimension insights.
                </div>
            </div>
        );
    }

    return (
        <div className="dim-composed-container">
            {/* Shared controls: Product + Dimension only (no TimeRange) */}
            <div className="dim-composed-controls">
                <ProductSelector
                    topicMap={topicMap}
                    selectedId={selectedTopic}
                    onSelect={selectTopic}
                />
                <FormControl size="small" variant="outlined">
                    <InputLabel id="dim-composed-select-label">Dimension</InputLabel>
                    <Select
                        labelId="dim-composed-select-label"
                        value={dimension}
                        label="Dimension"
                        onChange={(e) => setDimension(e.target.value)}
                        sx={{ '& .MuiSelect-select': { backgroundColor: 'white', paddingRight: '32px !important' } }}
                    >
                        {DIMENSIONS.map(d => (
                            <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {activeWidget && (
                <nav className="dim-breadcrumb" aria-label="Dimension dashboard navigation">
                    <button
                        type="button"
                        className="dim-breadcrumb-link"
                        onClick={() => setActiveWidget(null)}
                    >
                        ← Dashboard Overview
                    </button>
                    <span className="dim-breadcrumb-separator">/</span>
                    <span className="dim-breadcrumb-current">{WIDGET_LABELS[activeWidget]}</span>
                </nav>
            )}

            {/* Card Grid */}
            {!activeWidget && (
                <div className="dim-composed-grid">
                    <WidgetErrorBoundary key={`most-reviewed-eb-${dimension}`}>
                        {loading.nps ? <WidgetSkeleton /> : (
                            <MostReviewedWidget
                                data={mostReviewedData}
                                totalData={npsData}
                                dimConfig={dimConfig}
                                onClick={() => handleCardClick('most-reviewed')}
                                error={errors.nps}
                            />
                        )}
                    </WidgetErrorBoundary>

                    <WidgetErrorBoundary key={`best-worst-eb-${dimension}`}>
                        {loading.quarterly ? <WidgetSkeleton /> : (
                            <BestWorstQuarterWidget
                                gains={latestQuarterData.gains}
                                drops={latestQuarterData.drops}
                                quarter={latestQuarterData.quarter}
                                prevQuarter={latestQuarterData.prevQuarter}
                                onClick={() => handleCardClick('best-worst')}
                                error={errors.quarterly}
                            />
                        )}
                    </WidgetErrorBoundary>

                    <WidgetErrorBoundary key={`struggling-eb-${dimension}`}>
                        {loading.nps ? <WidgetSkeleton /> : (
                            <StrugglingStoresWidget
                                data={strugglingData}
                                totalStrugglingCount={allStrugglingData.length}
                                dimConfig={dimConfig}
                                onClick={() => handleCardClick('struggling')}
                                error={errors.nps}
                            />
                        )}
                    </WidgetErrorBoundary>
                </div>
            )}

            {/* Inline Detail Panel */}
            {activeWidget === 'most-reviewed' && (
                <div className="dim-detail-panel" role="region" aria-label="Most Reviewed Detail">
                    <MostReviewedWidget.Detail
                        data={npsData}
                        dimension={dimension}
                        topicId={selectedTopic}
                        dimConfig={dimConfig}
                        isLoading={loading.nps}
                        onClose={() => setActiveWidget(null)}
                    />
                </div>
            )}
            {activeWidget === 'best-worst' && (
                <div className="dim-detail-panel" role="region" aria-label="Quarter over Quarter Detail">
                    <BestWorstQuarterWidget.Detail
                        data={quarterlyData}
                        dimension={dimension}
                        topicId={selectedTopic}
                        dimConfig={dimConfig}
                        isLoading={loading.quarterly}
                        onClose={() => setActiveWidget(null)}
                    />
                </div>
            )}
            {activeWidget === 'struggling' && (
                <div className="dim-detail-panel" role="region" aria-label="Struggling Stores Detail">
                    <StrugglingStoresWidget.Detail
                        data={allStrugglingData}
                        dimension={dimension}
                        topicId={selectedTopic}
                        dimConfig={dimConfig}
                        isLoading={loading.nps}
                        onClose={() => setActiveWidget(null)}
                    />
                </div>
            )}
        </div>
    );
}

// Helper to derive previous quarter label from a quarter string like "2026-Q1"
function findPrevQuarter(quarter) {
    if (!quarter) return null;
    const match = quarter.match(/^(\d{4})-Q(\d)$/);
    if (!match) return null;
    let [, year, q] = match;
    year = parseInt(year); q = parseInt(q);
    if (q === 1) return `${year - 1}-Q4`;
    return `${year}-Q${q - 1}`;
}
